import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import { useOutletContext, useParams } from 'react-router-dom';
import cn from '@/utils/cn';
import Icon from '@/components/ui/Icon';
import apiErrorMessage from '../../shared/apiErrors';
import { panelClass } from '../../shared/formPanel';
import useProgramTree, { TreeLesson } from '../../shared/ProgramTree/useProgramTree';
import SPAButton from '../../shared/SPAButton';
import SPACheckbox from '../../shared/SPACheckbox';
import SPASectionHeading from '../../shared/SPASectionHeading';
import { useSPAToast } from '../../shared/SPAToast';
import { EditableUnit, useReorderUnitLessons, useUpdateUnit } from './useUnit';

const rowClass = cn(
  'tw-flex tw-items-center tw-gap-2 tw-rounded tw-py-1.5 tw-pr-1 tw-text-sm',
  'tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary first:tw-border-t-0',
);

const gripClass = cn(
  'tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded',
  'tw-cursor-grab active:tw-cursor-grabbing tw-text-spa-tertiary tw-transition-colors',
  'hover:tw-bg-spa-bg-hover hover:tw-text-spa-primary',
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-spa-accent',
);

const kbdClass = cn(
  'tw-rounded tw-border tw-border-solid tw-border-b-2 tw-border-spa-border-input tw-px-1',
  'tw-text-[0.6875rem] tw-text-spa-secondary',
);

const Kbd = ({ children }: { children: React.ReactNode }) => <kbd className={kbdClass}>{children}</kbd>;

const lessonLabel = (lesson: TreeLesson) => (
  lesson.title_number ? `${lesson.title_number}. ${lesson.title}` : lesson.title
);

// The legacy form's two lesson lists: the unit's lessons, reordered by drag and saved on drop as on
// the table of contents, with Remove; and the program's unitless lessons to add.
const UnitLessonsPanel = () => {
  const unit = useOutletContext<EditableUnit>();
  const { programId } = useParams<{ programId: string }>();
  const pid = Number(programId);
  const spaToast = useSPAToast();
  const tree = useProgramTree(pid);
  const updateUnit = useUpdateUnit(pid, unit.id);
  const reorderLessons = useReorderUnitLessons(pid);
  const [selected, setSelected] = useState<number[]>([]);
  // Committed on drop so the browser paints one state; cleared once the cache catches up.
  const [localOrder, setLocalOrder] = useState<TreeLesson[] | null>(null);
  const [reorderFailed, setReorderFailed] = useState(false);

  const unitLessons = tree.units.find(treeUnit => treeUnit.id === unit.id)?.lessons ?? [];
  const available = tree.ghostLessons;

  useEffect(() => {
    setLocalOrder(null);
  }, [tree.units]);

  const lessons = localOrder ?? unitLessons;

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const next = [...lessons];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setLocalOrder(next);
    setReorderFailed(false);

    reorderLessons(next)
      .then(() => spaToast.success('Saved lesson order.'))
      .catch(() => {
        setLocalOrder(null);
        setReorderFailed(true);
      });
  };

  const remove = (lesson: TreeLesson) => updateUnit.mutate({ unit: { remove_lesson_ids: [lesson.id] } }, {
    onSuccess: () => spaToast.success(`Removed “${lesson.title}” from the unit.`),
  });

  const addSelected = () => updateUnit.mutate({ unit: { lesson_ids: selected } }, {
    onSuccess: () => {
      spaToast.success(`Added ${selected.length} ${selected.length === 1 ? 'lesson' : 'lessons'} to the unit.`);
      setSelected([]);
    },
  });

  const toggle = (id: number) => setSelected(current => (
    current.includes(id) ? current.filter(item => item !== id) : [...current, id]
  ));

  return (
    <div className={panelClass}>
      <SPASectionHeading
        className="tw-mb-1"
        meta={(
          <span className="tw-text-xs tw-text-spa-tertiary">
            Drag the handle to reorder, or focus it and press <Kbd>Space</Kbd> then <Kbd>↑</Kbd> <Kbd>↓</Kbd>
          </span>
        )}
        title="Lessons in this unit"
      />
      {tree.isLoading && <p className="tw-m-0 tw-py-2 tw-text-sm tw-text-spa-tertiary">Loading lessons…</p>}
      {!tree.isLoading && lessons.length === 0 && (
        <p className="tw-m-0 tw-py-2 tw-text-sm tw-italic tw-text-spa-tertiary">No lessons in this unit yet.</p>
      )}
      {/* Row spacing is padding on each item, not a flex gap: react-beautiful-dnd lays siblings out
          by their box while one is lifted, so a gap would collapse. */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="unit-lessons">
          {droppableProvided => (
            <ol
              ref={droppableProvided.innerRef}
              className="tw-m-0 tw-list-none tw-p-0"
              {...droppableProvided.droppableProps}
            >
              {lessons.map((lesson, index) => (
                <Draggable key={lesson.id} draggableId={String(lesson.id)} index={index}>
                  {(dragProvided, snapshot) => (
                    <li
                      ref={dragProvided.innerRef}
                      className={cn(rowClass, snapshot.isDragging && 'tw-bg-spa-bg-secondary tw-shadow-lg')}
                      {...dragProvided.draggableProps}
                    >
                      <div
                        {...dragProvided.dragHandleProps}
                        aria-label={`Reorder ${lesson.title}`}
                        className={gripClass}
                      >
                        <Icon icon="Drag" size={16} />
                      </div>
                      <span className="tw-min-w-0 tw-flex-1 tw-text-spa-primary">{lessonLabel(lesson)}</span>
                      <SPAButton
                        aria-label={`Remove ${lesson.title} from the unit`}
                        disabled={updateUnit.isLoading}
                        size="xs"
                        variant="danger-outline"
                        onClick={() => remove(lesson)}
                      >
                        Remove
                      </SPAButton>
                    </li>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </ol>
          )}
        </Droppable>
      </DragDropContext>
      {reorderFailed && (
        <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-spa-error" role="alert">
          Couldn’t save the lesson order. Try again.
        </p>
      )}

      {/* Only when the program has lessons outside every unit; otherwise there is nothing to say. */}
      {available.length > 0 && (
        <>
          <hr className="tw-my-5 tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary" />
          <SPASectionHeading className="tw-mb-1" title="Add lessons" />
          <p className="tw-m-0 tw-mb-2 tw-text-xs tw-text-spa-tertiary">
            The program’s lessons that aren’t in any unit yet.
          </p>
          <ul className="tw-m-0 tw-list-none tw-p-0">
            {available.map(lesson => (
              <li key={lesson.id} className="tw-py-1.5">
                <SPACheckbox checked={selected.includes(lesson.id)} onChange={() => toggle(lesson.id)}>
                  {lessonLabel(lesson)}
                </SPACheckbox>
              </li>
            ))}
          </ul>
          <div className="tw-mt-3 tw-flex tw-items-center tw-gap-3">
            <SPAButton disabled={selected.length === 0 || updateUnit.isLoading} variant="primary" onClick={addSelected}>
              Add {selected.length > 0 ? `${selected.length} ` : ''}to unit
            </SPAButton>
          </div>
        </>
      )}
      {updateUnit.isError && (
        <p className="tw-m-0 tw-mt-3 tw-text-sm tw-text-spa-error" role="alert">
          {apiErrorMessage(updateUnit.error, 'Couldn’t update the unit’s lessons. Try again.')}
        </p>
      )}
    </div>
  );
};

export default UnitLessonsPanel;
